import React, { useMemo } from "react";

import dateformat from "dateformat";
import pluralize from "pluralize";
import moment from "moment";
import { ProgressBar } from "react-bootstrap";
import numeral from "numeral"

import Thumbnail from "src/components/thumbnail";
import { FileService } from "src/utils/file.service";
import AssetService from "src/utils/asset.service";
import Text from "src/components/text";

import { Course } from "src/types/coaching/courses";
import { Simulation } from "src/types/coaching/simulation";
import { Podcast } from "src/types/coaching/podcast";
import { Media } from "src/types/media";

import _ from "lodash";
import cx from "classnames";
import styles from "./card.module.scss";
import { CALL_TYPES } from "src/constants/simulation";
import Tooltip from "src/components/tooltip";
import Truncate from "src/components/truncate";
import UserAvatar from "src/pages/settings/components/userAvatar";

const defaultImage = "/images/collections/background.jpeg";
const defaultCourse = "/images/assets/courseThumbnail.jpg";
const defaultPodcast = "/images/assets/podcastThumbnail.jpg"
const defaultSimulation = "/images/assets/simulationThumbnail.jpg"


interface CourseCardProps {
  course: Course;
  onClick: (course: any) => void;
}

const CourseCard: React.FC<CourseCardProps> = ({ course, onClick }) => {
  const name = useMemo(() => _.get(course, "name", ""), [course]);
  const lessons = useMemo(() => _.get(course, "lessons",0), [course]);
  const assessment = useMemo(() => _.get(course, "assessments",0), [course]);
  const duration = useMemo(
    () => (_.get(course, "duration", 0)),
    [course],
  );

  const icon = useMemo(
    () => _.get(course, "banner.background") as Media,
    [course],
  );

  const position = useMemo(() => _.get(course, "banner.position"), [course]);

  const crop = useMemo(() => _.get(course, "banner.crop"), [course]);

  const createdBy = useMemo(
    () => _.get(course, "createdBy.icon", null),
    [course],
  );

  const createdDate = useMemo(
    () => _.get(course, "createdDate"),
    [course],
  );

  const createdDateValue = dateformat(Number(createdDate), "mmmm dd, yyyy");

  return (
    <div
      className={cx(styles.card, styles.courseCard)}
      onClick={() => onClick(course)}
    >
      <div className={styles.header}>
        <div className={cx(styles.top)}>
          <Thumbnail
            className={styles.icon}
            media={icon}
            defaultImage={defaultCourse}
            width={600}
            position={position}
            crop={crop}
          />
          <i className={cx("paperflite-book", styles.book)} />
        </div>
        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <i className="paperflite-clock"></i>
            <span className={styles.value}>
              {FileService.formatDuration(duration, "short")}
            </span>
          </div>

          {lessons > 0 && (
            <>
              <div className={styles.divider}></div>
              <div className={styles.metadata}>
                <i className="paperflite-document"></i>
                <span className={styles.value}>
                  {lessons} {pluralize("lesson", lessons)}
                </span>
              </div>
            </>
          )}
          {assessment > 0 && (
            <>
              <div className={styles.divider}></div>
              <div className={styles.metadata}>
                <i className="paperflite-assessment"></i>
                <span className={styles.value}>
                  {assessment} {pluralize("assessment", assessment)}
                </span>
              </div>
            </>
          )}
        </div>
        <div className={styles.details}>
          <Text className={styles.name} text={name || ""} lines={2} />
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.metadata}>
          {" "}
          <div className={styles.metadata_author}>
            <UserAvatar
                width={"1.5rem"}
                height={"1.5rem"}
                src={_.get(createdBy, "full", "")}
              />
            <span className={styles.days}>Added on {createdDateValue}</span>
          </div>
        </div>
        <button className={cx(styles.action, styles.start)}>
          <i className="paperflite-right-arrow" />
          <span>start learning</span>
        </button>
      </div>
    </div>
  );
};

interface SimulationCardProps {
  course: Simulation;
  onClick: (course: any) => void;
}

const SimulationCard: React.FC<SimulationCardProps> = ({
  course,
  onClick,
}) => {
  const name = useMemo(() => _.get(course, "name", ""), [course]);
  const icon = useMemo(
    () => _.get(course, "banner.background") as Media,
    [course],
  );
  

  const position = useMemo(() => _.get(course, "banner.position"), [course]);

  const crop = useMemo(() => _.get(course, "banner.crop"), [course]);

  const createdBy = useMemo(
    () => _.get(course, "createdBy.icon", null),
    [course],
  );

  const callType = useMemo(() => _.get(course, "callType", ""), [course]);
  const industry = useMemo(
    () => _.get(course, "organization.industry", ""),
    [course],
  );

  const getCallType = useMemo(() => {
    return CALL_TYPES.find((item) => item.id === callType)?.title || "";
  }, [callType]);

  const duration = useMemo(() => _.get(course, "duration"), [course]);

  const timeBound = useMemo(() => _.get(course,"timeBound"),[course]);

     const getCourseCreated = () => {
      const created = moment(AssetService.getNumericValue(_.get(course, "createdDate")));
      const today = moment();
    
      const diffInDays = today.diff(created, "days");
    
      if (diffInDays === 0) return "today";
      if (diffInDays === 1) return "1 day ago";
      if (diffInDays < 7) return `${diffInDays} days ago`;
    
      return created.format("MMM D, YYYY");
    };

  return (
    <div
      className={cx(styles.card, styles.simulationCard)}
      onClick={() => onClick(course)}
    >
      <div className={styles.header}>
        <div className={cx(styles.top)}>
          <div className={styles.banner}>
            <Thumbnail
              className={cx(styles.icon, {
                [styles.discovery]: callType === "discovery",
                [styles.coldcall]: callType === "cold_call",
                [styles.elevatorPitch]: callType === "elevator_pitch",
              })}
              media={icon}
              defaultImage={defaultSimulation}
              width={600}
              position={position}
              crop={crop}
            />
            <div className={styles.phone}>
              <i className={cx("paperflite-phone",{
                [styles.discovery]: callType === "discovery",
                [styles.coldcall]: callType === "cold_call",
                [styles.elevatorPitch]: callType === "elevator_pitch",
              })} />
            </div>
          </div>
          <div className={styles.type}>
            <i className="paperflite-phone-call" />
            <p className={styles.name}>{getCallType}</p>
          </div>
        </div>
        <div className={styles.metadatas}>
          <div className={styles.metadata}>
          <i className="paperflite-clock"></i>
          <span className={styles.value}>
              {callType === "elevator_pitch"
                ? FileService.formatTimeBound(timeBound || 0,"short")
                : `${duration} m`}
          </span>
          </div>
          {industry && <div className={styles.divider}></div>}
          {industry && (
            <div className={styles.metadata}>
              <i className="paperflite-industry"></i>
              <Text text={industry || ""} className={styles.industry} lines={1} />
            </div>
          )}
        </div>
        <div className={styles.details}>
          <Text className={styles.name} text={name || ""} lines={2} />
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.metadata}>
          <div className={styles.metadata_author}>
            <UserAvatar
              width={"1.5rem"}
              height={"1.5rem"}
              src={_.get(createdBy, "full", "")}
              />
              <span>
              by
              </span>
          </div>
          <div className={styles.metadata_details}>
              <Tooltip content={_.get(course, "createdBy.firstName", "")} placement='top'>
                <Truncate lines={1} width={120}>
                  {_.get(course, "createdBy.firstName", "")},
                </Truncate>
              </Tooltip>
            <span>
              {getCourseCreated()}
            </span>
          </div>
        </div>
        <button className={cx(styles.action, styles.start)}>
          <i className="paperflite-right-arrow" />
          <span>open simulation</span>
        </button>
      </div>
    </div>
  );
};


const PlaceholderSimulationCard: React.FC<object> = () => {
  return (
    <div className={cx(styles.simulationCard, styles.placeholder)}>
      <div className={cx(styles.header)}>
        <span className={cx(styles.day, styles.block)}></span>
        <span className={cx(styles.day, styles.block)}></span>
      </div>
      <div className={styles.body}>
        <div className={cx(styles.heading, styles.block)}></div>
        <p className={cx(styles.summary, styles.block)}></p>
      </div>

      <div className={styles.footer}>
        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <div className={styles.block}></div>
          </div>
          <div className={styles.divider}></div>
          <div className={styles.metadata}>
            <div className={styles.block}></div>
          </div>
          <div className={styles.divider}></div>
          <div className={styles.metadata}>
            <div className={styles.block}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PlaceholderCourseCard: React.FC = () => {
  return (
    <div className={cx(styles.card, styles.placeholder)}>
      <div className={styles.header}>
        <div className={cx(styles.icon, styles.block)}></div>
      </div>
      <div className={styles.details}>
        <div className={cx(styles.name, styles.block)}></div>
        <p className={cx(styles.description)}>
          <div className={styles.block}></div>
        </p>
      </div>
      <div className={styles.footer}>
        <div className={styles.block}></div>
      </div>
    </div>
  );
};

//Podcast
interface PodcastCardProps {
  course: Podcast;
  onClick: (course: any) => void;
}

const PodcastCard: React.FC<PodcastCardProps> = ({
  course,
  onClick,
}) => {
  const name = useMemo(() => _.get(course, "name", ""), [course]);
  const icon = useMemo(
    () => _.get(course, "banner.background") as Media,
    [course],
  );

  const position = useMemo(() => _.get(course, "banner.position"), [course]);

  const crop = useMemo(() => _.get(course, "banner.crop"), [course]);

  const createdBy = useMemo(
    () => _.get(course, "createdBy.icon", null),
    [course],
  );

  const podcastType = useMemo(() => _.get(course, "type"), [course]);

  const duration = useMemo(() => _.get(course, "duration", 0), [course]);

    const getCourseCreated = () => {
      const created = moment(AssetService.getNumericValue(_.get(course, "createdDate")));
      const today = moment();
    
      const diffInDays = today.diff(created, "days");
    
      if (diffInDays === 0) return "today";
      if (diffInDays === 1) return "1 day ago";
      if (diffInDays < 7) return `${diffInDays} days ago`;
    
      return created.format("MMM D, YYYY");
    };

  return (
    <div
      className={cx(styles.card, styles.podcastCard)}
      onClick={() => onClick(course)}
    >
      <div className={styles.header}>
        <div className={cx(styles.top)}>
          {" "}
          <div className={styles.banner}>
            <Thumbnail
              className={styles.icon}
              media={icon}
              defaultImage={defaultPodcast}
              width={600}
              position={position}
              crop={crop}
            />
            <i className={cx("paperflite-micro-phone", styles.microphone)} />
          </div>
          <div className={styles.type}>
            <i className="paperflite-waveform" />
            <p className={styles.name}>{podcastType}</p>
          </div>
        </div>
        <div className={styles.metadatas}>
          <div className={styles.metadata} >
          <i className="paperflite-clock"></i>
          <span className={styles.value}>
            {FileService.formatDuration(duration)}
          </span>
          </div>
        </div>
        <div className={styles.details}>
          <Text className={styles.name} text={name || ""} lines={2} />
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.metadata}>
          <div className={styles.metadata_author}>
           <UserAvatar
              width={"1.5rem"}
              height={"1.5rem"}
              src={_.get(createdBy, "full", "")}
            />
            <span> 
              by
            </span>
          </div>
          <div className={styles.metadata_details}>
            <Tooltip content={_.get(course, "createdBy.firstName", "")} placement='top'>
              <Truncate lines={1} width={120}>
                {_.get(course, "createdBy.firstName","")},
              </Truncate>
            </Tooltip>
            <span>
              {getCourseCreated()}
            </span>
          </div>
        </div>
        <button className={cx(styles.action, styles.start)}>
          <i className="paperflite-right-arrow" />
          <span>try now</span>
        </button>
      </div>
    </div>
  );
};

interface ContinueLearningCardProps {
  course: Course;
  onClick: (course: Course) => void;
}

const ContinueLearningCard: React.FC<ContinueLearningCardProps> = ({
  course,
  onClick,
}) => {
  const name = useMemo(() => _.get(course, "course.name", ""), [course]);
  const icon = useMemo(
    () => _.get(course, "course.banner.background") as unknown as Media,
    [course],
  );

  const position = useMemo(() => _.get(course, "course.banner.position"), [
    course,
  ]);

  const crop = useMemo(() => _.get(course, "course.banner.crop"), [course]);

  const entityType = useMemo(() => _.get(course,"course.entityType",null),[course])

  const getProgress = (course: Course) => {
    const total = _.get(course, "course.duration", 0);
    const remaining = _.get(course, "renamingDuration", 0);

    if (remaining > 0) {
      const completed = ((total - remaining) / total) * 100;
      return `${Math.round(completed)}% completed`;
    }
    return "0% completed";
  };

  const progress = getProgress(course);

  const completed = useMemo(() => _.get(course, "completedCount", 0),[course])
  const lessons = useMemo(() => _.get(course, "course.lessons", 0), [course]);
   const assessment = useMemo(
      () => _.get(course, "course.assessments", 0),
      [course],
    );
  const totalLessons = lessons + assessment;
  const duration = useMemo(() => _.get(course, "course.duration", 0), [course]);

  const percentCompleted = useMemo(()=>_.get(course, 'percentCompleted', 0),[course]);

  const completedDuration = useMemo(()=>_.get(course,'progressCompleted',0),[course])

  const score = useMemo(() => {
      const percentage = Math.round((completed / totalLessons) * 100) ;
      return percentage;
    }, [course]);

  const getDefaultImage = (entityType?: string) => {
    switch (entityType) {
      case "podcast":
        return defaultPodcast;
      case "simulation":
        return defaultSimulation;
      case "course":
        return defaultCourse;
      default:
        return defaultCourse;
    }
  };


  return (
    <div
      className={cx(
        styles.card,
        styles.continue,
        entityType === "course" ? styles.courseCard : styles.podcastCard,
      )}
      onClick={() => onClick(course)}
    >
      <div className={cx(styles.header, styles[entityType!])}>
        <Thumbnail
          className={cx(
            entityType === "podcast" || entityType === "simulation"
              ? styles.podcast
              : styles.course,
          )}
          media={icon}
          defaultImage={getDefaultImage(entityType!)}
          width={600}
          position={position}
          crop={crop}
        />

        {entityType === "podcast" && (
          <span className={styles.podcastSpan}>
            {FileService.formatTime(completedDuration)} /{" "}
            {FileService.formatTime(duration)}
          </span>
        )}

        {entityType === "course" && (
          <span className={styles.courseSpan}>
            {numeral(completed).format("00")}/
            {numeral(totalLessons).format("00")}
          </span>
        )}
      </div>
      <div className={styles.details}>
        <Text className={styles.name} text={name || ""} lines={1} />
        <div className={styles.footer}>
          {entityType === "simulation" && (
            <div className={styles.metadatas}>
              <div className={styles.metadata}>
                <span>{progress}</span>
              </div>
            </div>
          )}

          {(entityType === "course" || entityType === "podcast") && (
            <div className={styles.progressBar}>
              <ProgressBar
                now={
                  (entityType === "course" && score) ||
                  (entityType === "podcast" && percentCompleted) ||
                  0
                }
              />
              <span className={styles.value}>
                {Math.round(
                  (entityType === "course" && score) ||
                  (entityType === "podcast" && percentCompleted) ||
                  0
                ) || 0} %
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PlaceholderContinueLearningCard: React.FC = () => {
  return (
    <div className={cx(styles.continuePlaceholder)}>
      <div className={cx(styles.header)}>
        <div className={cx(styles.icon, styles.block)}></div>
         <div className={styles.details}>
          <div className={cx(styles.name, styles.block)}></div>
          <div className={cx(styles.summary, styles.block)}></div>
         </div>
      </div>
    </div>
  );
};

interface PopularLearningCardProps {
  item: any;
  handleClick?: () => void;
}

const PopularLearningCard: React.FC<PopularLearningCardProps> = ({ item, handleClick }) => {
  const courseName = _.get(item.course, "name", "");
  const icon = _.get(item.course, "banner.background") as Media;
  const position = _.get(item.course, "banner.position");
  const crop = _.get(item.course, "banner.crop");

  return (
    <div className={cx(styles.card)} onClick={handleClick}>
      <div className={cx(styles.header)}>
        <div className={styles.icon}>
          <Thumbnail media={icon} defaultImage={defaultImage} width={370} position={position} crop={crop}/>
        </div>
      </div>

      <div className={styles.content}>
        <Text className={styles.name} text={courseName || ""} lines={1} />

        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <span className={styles.label}>
              {_.capitalize(item?.type) || "Course"}
            </span>
          </div>
          <div className={styles.border} />
          <div className={styles.metadata}>
            <i className="paperflite-assessment"></i>
            <span
              className={styles.label}
            >{`${item?.enrolled || 0} users enrolled`}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export {
  SimulationCard,
  CourseCard,
  PodcastCard,
  PopularLearningCard,
  ContinueLearningCard,
  PlaceholderSimulationCard,
  PlaceholderCourseCard,
  PlaceholderContinueLearningCard
};
